import React, { FC, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Flex, Text } from "@radix-ui/themes";
import { DateTime } from "luxon";
import { UuidFieldRow } from "@/components/table/uuid-field-row";

import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";

type ApiRequestRecordSummarySchema = {
  id: string;
  associated_space_account_id?: string | null;
  request_kind: string;
  request_status: string;
  started_at?: string | null;
  ended_at?: string | null;
  ended_with_error?: boolean | null;
  created_at: string;
  updated_at: string;
};

export const ApiRequestRecordsRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = 50;
  const initialQuery = searchParams.get("query") ?? "";
  const [searchTerm, setSearchTerm] = useState<string>(initialQuery);
  const [inputValue, setInputValue] = useState<string>(initialQuery);
  const isUuid = (val: string) =>
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(
      val
    );

  useEffect(() => {
    const query = searchParams.get("query") ?? "";
    setSearchTerm(query);
    setInputValue(query);
  }, [searchParams]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["api-request-records-search", searchTerm],
    queryFn: async () => {
      const term = searchTerm.trim();
      if (!term) return { data: { results: [], total: 0 } };

      // Backend supports unified UUID query for id or space_account_id
      if (!isUuid(term)) return { data: { results: [], total: 0 } };

      const resp = await api.post("/internal/v1/api-request-records/search", {
        body: { limit: pageSize, query: term },
      });
      return resp;
    },
    enabled: searchTerm.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const handleSearch = (query: string) => {
    if (query) {
      searchParams.set("query", query);
    } else {
      searchParams.delete("query");
    }
    setSearchParams(searchParams);
  };

  const handleView = (id: string) => {
    navigate(atlasRoutes.apiRequestRecordsView({ params: { apiRequestRecordId: id } }).path);
  };

  const tableData = useMemo<ApiRequestRecordSummarySchema[]>(
    () => data?.data?.results ?? [],
    [data]
  );

  return (
    <Container>
      <Flex justify="between" align="center" mb="4">
        <div>
          <h1>API Request Records</h1>
          <DescriptionText>Search by ID or Space Account ID.</DescriptionText>
        </div>
      </Flex>

      <SearchRow>
        <input
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") handleSearch(inputValue);
          }}
          placeholder="Search by Record ID or Space Account ID"
        />
        <Button onClick={() => handleSearch(inputValue)}>Search</Button>
      </SearchRow>

      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching records: {error.message}</div>}

      {tableData.length > 0 ? (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Created At</th>
                <th>ID</th>
                <th>Space Account</th>
                <th>Kind</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map(row => (
                <tr key={row.id}>
                  <td>
                    {row.created_at
                      ? DateTime.fromISO(row.created_at).toFormat("MMM d, yyyy h:mm a")
                      : "-"}
                  </td>
                  <td>
                    <UuidFieldRow id={row.id as unknown as string} />
                  </td>
                  <td>
                    <UuidFieldRow
                      id={(row.associated_space_account_id as unknown as string) ?? null}
                    />
                  </td>
                  <td>{row.request_kind}</td>
                  <td>{row.request_status}</td>
                  <td>
                    <Button variant="soft" onClick={() => handleView(row.id)}>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      ) : (
        !isLoading && searchTerm && <div>No records found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;

const SearchRow = styled(Flex)`
  gap: 8px;
  margin: 12px 0 16px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th,
  td {
    text-align: left;
    padding: 8px 6px;
    border-bottom: 1px solid var(--gray-5);
    white-space: nowrap;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;
